// @flow
import React, {useEffect, useState} from 'react';
import {Typography, ButtonGroup, Button, TextField} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import CustomModal from '../../../../elements/CustomModal/CustomModal';
import {useDebounce} from '../../../../utils/react';
import type {Product} from '../../../../entities';
import {Remove, Add} from '@material-ui/icons';
import {getImageUrl} from '../../../../utils/image_provider';
import {colors} from '../../../../assets/styles/colors';
import type {EditProductVariantRequest} from '../../api';

type Props = {|
  open: boolean,
  handleClose: () => void,
  product?: Product,
  handleChangeProductStock: (stock: number) => void,
  editProductVariant: ({
    editProductVariantRequest: EditProductVariantRequest,
  }) => void,
  isSaving?: boolean,
|};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyItems: 'center',
    alignItems: 'center',
    textAlign: 'center',
    // width: 320,
    // height: 240,
    padding: theme.spacing(4),
  },
  productContainer: {
    height: '70%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.375rem',
  },
  productWrapper: {
    marginTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  product: {
    display: 'flex',
    backgroundColor: colors.grey200,
    padding: theme.spacing(1),
    borderRadius: '12px',
  },
  productInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  productName: {
    marginLeft: theme.spacing(1),
    textAlign: 'left',
    fontSize: '0.875rem',
  },
  imageBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 64,
    width: 64,
    borderRadius: '12px',
    overflow: 'hidden',
  },
  image: {
    // width: '100%',
    height: 'auto',
    // borderRadius: theme.shape.borderRadius,
    /*boxShadow: theme.shadows[2],*/
    maxHeight: '100%',
    objectFit: 'contain',
  },
  inputRow: {
    marginTop: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    width: 240,
    // maxHeight: 40,
  },
  // iconButton: {
  //   backgroundColor: colors.grey300,
  //   '&:hover': {
  //     backgroundColor: colors.grey500,
  //   },
  // },
  textInput: {
    textAlign: 'center',
  },
}));

export default function StockChangeModal(props: Props) {
  const {
    open,
    handleClose,
    product,
    handleChangeProductStock,
    editProductVariant,
    isSaving,
  } = props;

  const classes = useStyles();

  const [ready, setReady] = useState(false);

  const [stock, setStock] = useState<number | number[]>(0);
  const debouncedStock = useDebounce(stock, 300);

  useEffect(() => {
    return () => {
      setStock(0);
    };
  }, []);

  useEffect(() => {
    if (product && ready) {
      if (typeof debouncedStock === 'number') {
        if (
          product.stockSetting &&
          product.stockSetting.enabled &&
          debouncedStock !== product.stockSetting.stock
        )
          handleChangeProductStock(debouncedStock);
      } else if (Array.isArray(stock)) {
        stock.forEach((s, i) => {
          if (
            product.productVariantSetting?.variants &&
            product.productVariantSetting?.variants[i].id &&
            s !== product.productVariantSetting?.variants[i].stock
          )
            editProductVariant({
              editProductVariantRequest: {
                productId: product.id,
                variantId: product.productVariantSetting?.variants[i].id,
                request: {stock: s},
              },
            });
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedStock]);

  useEffect(() => {
    if (open && product) {
      if (
        product.productVariantSetting?.enabled &&
        product.productVariantSetting.variants &&
        product.productVariantSetting.variants.length > 0
      ) {
        const aux = [];
        product.productVariantSetting.variants.forEach(v => {
          aux.push(v.stock || 0);
        });
        setStock(aux);
      } else if (product.stockSetting && product.stockSetting.enabled) {
        setStock(product.stockSetting.stock);
      }
    }
  }, [product, open]);

  const handleChangeStock = n => {
    setReady(true);
    setStock(prev => {
      if (typeof prev !== 'number') return prev;
      return prev + n;
    });
  };

  const handleSetStock = e => {
    e.persist();
    setReady(true);
    setStock(prev => {
      if (typeof prev !== 'number') return prev;
      return parseInt(e.target.value);
    });
  };

  const handleChangeVariantStock = (i, n) => {
    setReady(true);
    setStock(prev => {
      if (!Array.isArray(prev)) return prev;
      const aux = [...prev];
      if (typeof n === 'string') aux[i] = n;
      if (typeof n === 'number') aux[i] = aux[i] + n;
      return aux;
    });
  };

  const handleCloseModal = () => {
    setReady(false);
    setStock(0);
    handleClose();
  };

  return (
    <CustomModal open={open} handleCloseModal={handleCloseModal}>
      <div className={classes.root}>
        <Typography className={classes.title}>
          Editor rápido de stock
        </Typography>
        <div className={classes.productContainer}>
          {product &&
          product.productVariantSetting?.enabled &&
          product.productVariantSetting?.variants &&
          product.productVariantSetting.variants.length > 0
            ? product.productVariantSetting.variants.map(
                (variant, i) =>
                  Array.isArray(stock) && (
                    <div className={classes.productWrapper}>
                      <div className={classes.product}>
                        <div className={classes.imageBox}>
                          <img
                            className={classes.image}
                            src={getImageUrl(variant.imageId)}
                            alt="no artifact that depicts visual perception for you"
                          />
                        </div>
                        <div className={classes.productInfo}>
                          <Typography className={classes.productName}>
                            {product.name}
                          </Typography>
                          <Typography className={classes.productName}>
                            {Object.values(variant.combinations).join('/')}
                          </Typography>
                        </div>
                      </div>
                      <div className={classes.inputRow}>
                        <ButtonGroup size="small">
                          <Button
                            size="small"
                            className={classes.iconButton}
                            onClick={() => handleChangeVariantStock(i, -1)}
                            disabled={isSaving || stock[i] <= 0}
                          >
                            <Remove fontSize="small" />
                          </Button>
                          <TextField
                            className={classes.textField}
                            inputProps={{className: classes.textInput}}
                            value={stock[i]}
                            onChange={e =>
                              handleChangeVariantStock(i, e.target.value)
                            }
                            type="number"
                            disabled={isSaving}
                            min="0"
                            step="1"
                          />
                          <Button
                            size="small"
                            className={classes.iconButton}
                            onClick={() => handleChangeVariantStock(i, 1)}
                            disabled={isSaving}
                          >
                            <Add fontSize="small" />
                          </Button>
                        </ButtonGroup>
                      </div>
                    </div>
                  )
              )
            : typeof stock === 'number' && (
                <div className={classes.productWrapper}>
                  <div className={classes.product}>
                    <div className={classes.imageBox}>
                      <img
                        className={classes.image}
                        src={
                          product && product.mainImageId
                            ? getImageUrl(product.mainImageId)
                            : ''
                        }
                        alt="no artifact that depicts visual perception for you"
                      />
                    </div>
                    <div className={classes.productInfo}>
                      <Typography className={classes.productName}>
                        {product && product.name}
                      </Typography>
                    </div>
                  </div>
                  <div className={classes.inputRow}>
                    <ButtonGroup size="small">
                      <Button
                        size="small"
                        className={classes.iconButton}
                        onClick={() => handleChangeStock(-1)}
                        // disabled={isSaving}
                      >
                        <Remove fontSize="small" />
                      </Button>
                      <TextField
                        className={classes.textField}
                        inputProps={{className: classes.textInput}}
                        value={stock}
                        onChange={handleSetStock}
                        type="number"
                        // disabled={isSaving}
                        min="0"
                        step="1"
                      />
                      <Button
                        size="small"
                        className={classes.iconButton}
                        onClick={() => handleChangeStock(1)}
                        // disabled={isSaving}
                      >
                        <Add fontSize="small" />
                      </Button>
                    </ButtonGroup>
                  </div>
                </div>
              )}
        </div>
      </div>
    </CustomModal>
  );
}
